import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  type BoxProps,
  HStack,
  Icon,
  ProgressCircle,
  VStack,
} from '@chakra-ui/react'
import { AlertTriangle, CheckCircle, Info } from 'lucide-react'
import React, { useMemo } from 'react'
import {
  MangoAlertVariant,
  useMangoAlertStyles,
} from '~/components/shared/componentStyles/MangoAlert.style'
import { MangoCloseButtonVariant } from '~/components/shared/componentStyles/MangoCloseButton.style'
import { MangoCloseButton } from '~/components/shared/MangoCloseButton'
import { NotificationDisposition } from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'

type MangoAlertProps = BoxProps & {
  title?: string
  status: NotificationDisposition
  description?: string | React.ReactNode
  showCloseButton?: boolean
  closeOnClick?: boolean
  onClose?: () => void
}

const getVariantFromStatus = (status: NotificationDisposition) => {
  switch (status) {
    case NotificationDisposition.Success:
      return MangoAlertVariant.Success
    case NotificationDisposition.Error:
      return MangoAlertVariant.Error
    case NotificationDisposition.Warning:
      return MangoAlertVariant.Warning
    case NotificationDisposition.Info:
      return MangoAlertVariant.Info
    case NotificationDisposition.Loading:
      return MangoAlertVariant.Loading
    default:
      return MangoAlertVariant.Info
  }
}

export const MangoAlert = React.forwardRef<HTMLDivElement, MangoAlertProps>(
  (props: MangoAlertProps, ref) => {
    const {
      status,
      description,
      title,
      showCloseButton,
      closeOnClick,
      onClose,
      ...rest
    } = props

    const variant = useMemo(() => getVariantFromStatus(status), [status])
    const styles = useMangoAlertStyles({ variant })

    const icon = useMemo(() => {
      switch (status) {
        case NotificationDisposition.Success:
          return (
            <Icon css={styles.icon}>
              <CheckCircle />
            </Icon>
          )
        case NotificationDisposition.Error:
        case NotificationDisposition.Warning:
          return (
            <Icon css={styles.icon}>
              <AlertTriangle />
            </Icon>
          )
        case NotificationDisposition.Info:
          return (
            <Icon css={styles.icon}>
              <Info />
            </Icon>
          )
        case NotificationDisposition.Loading:
          return (
            <ProgressCircle.Root
              colorPalette={styles.circleProgress.color!.toString()}
              size={styles.circleProgress.height!.toString()}
            >
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          )
        default:
          return <></>
      }
    }, [status, styles.icon, styles.circleProgress])

    return (
      <Box
        ref={ref}
        as={Alert.Root}
        css={styles.container}
        onClick={closeOnClick ? onClose : undefined}
        cursor={closeOnClick ? 'pointer' : undefined}
        {...rest}
      >
        <HStack css={styles.contentContainer}>
          {icon}
          <VStack
            css={styles.textContainer}
            alignItems={styles.textContainer?.alignItems}
            gap={styles.textContainer?.gap}
          >
            {title && <AlertTitle css={styles.title}>{title}</AlertTitle>}
            {description && (
              <AlertDescription css={styles.description}>
                {description}
              </AlertDescription>
            )}
          </VStack>
        </HStack>
        {showCloseButton && (
          <MangoCloseButton
            css={styles.closeButton}
            onClick={onClose}
            size={Size.md}
            variant={MangoCloseButtonVariant.AlertWindow}
          />
        )}
      </Box>
    )
  },
)
